import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, Zap, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SettingsLayout } from './SettingsLayout';
import { getApiKeyStatus, generateApiKey } from '@/api/settings';

export function ApiAccessPage() {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['api-key-status'],
    queryFn: getApiKeyStatus,
  });

  const mutation = useMutation({
    mutationFn: generateApiKey,
    onSuccess: (result) => {
      if (result.success && result.api_key) {
        setApiKey(result.api_key);
        refetch();
      }
    },
  });

  useEffect(() => {
    setApiKey(null);
  }, []);

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  if (error || !data) {
    return (
      <SettingsLayout>
        <Alert variant="destructive">
          <AlertDescription>Unable to load API access details.</AlertDescription>
        </Alert>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            API Access
          </CardTitle>
          <CardDescription>
            Generate an API key to access doploy programmatically. Keys are shown only once.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription className="flex gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-600" />
              Keep your API key secure. Anyone with the key can control your account.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Status</p>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded bg-gray-100 text-sm">
                {data.has_api_key ? 'API key exists' : 'No API key yet'}
              </span>
            </div>
          </div>

          {apiKey ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Your new API key (save it now)</p>
              <code className="block bg-gray-900 text-white px-3 py-2 rounded text-sm break-all">
                {apiKey}
              </code>
            </div>
          ) : (
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate New API Key'
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
